// /admin/usuarios-admin/audit-log — log paginado de eventos admin (super-admin only).
//
// Filtros: action (whitelist), admin_id (input). Paginación simple (offset/limit).

import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { FileText, ArrowLeft, AlertCircle, Loader2, ChevronLeft, ChevronRight } from 'lucide-react';

import { getAdminAuditLog } from '../../lib/adminApi';

function localePara(lang) {
  const map = { es: 'es-ES', en: 'en-GB', ru: 'ru-RU', fr: 'fr-FR' };
  return map[lang] || 'es-ES';
}

function fmtFechaHora(iso, lang) {
  if (!iso) return '—';
  const d = new Date(iso);
  return new Intl.DateTimeFormat(localePara(lang), {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit',
  }).format(d);
}

const ACCIONES = [
  '', 'admin_invitado', 'admin_activado', 'admin_desactivado', 'admin_reactivado',
  'admin_editado', 'admin_password_reset', 'permisos_cambiados', 'invitacion_revocada',
];

const PAGE_SIZE = 50;

export default function AdminUsuariosAdminAuditLog() {
  const { t, i18n } = useTranslation();
  const lang = (i18n.resolvedLanguage || 'es').toLowerCase();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filtroAccion, setFiltroAccion] = useState('');
  const [offset, setOffset] = useState(0);

  const cargar = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { limit: PAGE_SIZE, offset };
      if (filtroAccion) params.action = filtroAccion;
      const d = await getAdminAuditLog(params);
      setData(d);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [filtroAccion, offset]);

  useEffect(() => { cargar(); }, [cargar]);

  // Reset offset al cambiar filtro
  useEffect(() => { setOffset(0); }, [filtroAccion]);

  const total = data?.total || 0;
  const hayAnt = offset > 0;
  const hayProx = offset + PAGE_SIZE < total;

  return (
    <div className="admin-page">
      <header className="admin-page-head">
        <div>
          <Link to="/admin/usuarios-admin" className="btn-link-back">
            <ArrowLeft size={14} /> {t('admin.audit_log.volver')}
          </Link>
          <h1 className="admin-page-titulo">
            <FileText size={22} strokeWidth={1.5} /> {t('admin.audit_log.titulo')}
          </h1>
          <p className="admin-page-sub">{t('admin.audit_log.sub')}</p>
        </div>
        <div className="admin-filtros-inline">
          <label className="admin-label">
            {t('admin.audit_log.filtro_accion')}{' '}
            <select value={filtroAccion} onChange={(e) => setFiltroAccion(e.target.value)}>
              {ACCIONES.map((a) => (
                <option key={a} value={a}>{a ? t('admin.audit_log.acciones.' + a, { defaultValue: a }) : t('admin.audit_log.todas')}</option>
              ))}
            </select>
          </label>
        </div>
      </header>

      {loading && (
        <div className="admin-loading"><Loader2 size={20} className="spin" /> <span>{t('admin.cargando')}</span></div>
      )}
      {error && (
        <div className="admin-error"><AlertCircle size={20} /><span>{error.message}</span></div>
      )}

      {!loading && !error && data && data.data.length === 0 && (
        <div className="admin-empty">{t('admin.audit_log.vacio')}</div>
      )}

      {!loading && !error && data && data.data.length > 0 && (
        <>
          <div className="admin-card">
            <table className="admin-tabla">
              <thead>
                <tr>
                  <th>{t('admin.audit_log.col_timestamp')}</th>
                  <th>{t('admin.audit_log.col_accion')}</th>
                  <th>{t('admin.audit_log.col_admin')}</th>
                  <th>{t('admin.audit_log.col_target')}</th>
                  <th>{t('admin.audit_log.col_details')}</th>
                  <th>{t('admin.audit_log.col_ip')}</th>
                </tr>
              </thead>
              <tbody>
                {data.data.map((e) => (
                  <tr key={e.id}>
                    <td className="admin-celda-tenue">{fmtFechaHora(e.timestamp, lang)}</td>
                    <td>
                      <span className="admin-pill admin-pill--neutro">
                        {t('admin.audit_log.acciones.' + e.action, { defaultValue: e.action })}
                      </span>
                    </td>
                    <td className="admin-celda-tenue">{e.admin_id ? <code style={{ fontSize: 11 }}>{e.admin_id.slice(0, 8)}…</code> : <em>{t('admin.audit_log.sistema')}</em>}</td>
                    <td className="admin-celda-tenue">{e.target_user_id ? <code style={{ fontSize: 11 }}>{e.target_user_id.slice(0, 8)}…</code> : '—'}</td>
                    <td className="admin-celda-tenue" style={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      <code style={{ fontSize: 11 }}>{JSON.stringify(e.details || {})}</code>
                    </td>
                    <td className="admin-celda-tenue">{e.ip_address || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="admin-paginacion">
            <button type="button" className="btn-secondary btn-mini" disabled={!hayAnt} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>
              <ChevronLeft size={14} /> {t('admin.audit_log.anterior')}
            </button>
            <span className="admin-celda-tenue">
              {t('admin.audit_log.pagina_info', {
                desde: offset + 1,
                hasta: Math.min(offset + PAGE_SIZE, total),
                total,
              })}
            </span>
            <button type="button" className="btn-secondary btn-mini" disabled={!hayProx} onClick={() => setOffset(offset + PAGE_SIZE)}>
              {t('admin.audit_log.siguiente')} <ChevronRight size={14} />
            </button>
          </div>
        </>
      )}
    </div>
  );
}
